import {contactMuted} from './contact-names.mjs';
import {Pencil,BellOff} from 'lucide-react';
import {shownName} from './profileTheme';
import {IdentityBadges} from './IdentityBadges';
import {avatarSrc as defaultAvatarSrc} from './avatar';
import {StaffMark} from './StaffMark';
import React, { useEffect, useState } from 'react';
import { Search, UserPlus, MessageCircle, Check, X, MoreHorizontal, Users, Mail, User, UserMinus, Copy, Ban } from 'lucide-react';
import { MenuAction } from './ContextMenu';
import { copyToClipboard, useLang } from './i18n';

export function FriendsView({
  tab,
  data,
  me,
  request,
  refresh,
  onMessage,
  onProfile,
  onRemove,
  onRename,
  onOpenMenu
}: {
  tab: string;
  data: any;
  me: any;
  request: any;
  refresh: () => Promise<void>;
  onMessage: (p: any) => void;
  onProfile: (p: any) => void;
  onRemove: (f: any) => void;
  onRename?:(p:any)=>void;
  onOpenMenu?: (x: number, y: number, actions: MenuAction[]) => void;
}) {
  const [, , tr] = useLang();
  const [query, setQuery] = useState('');
  const [username, setUsername] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState('');
  // Outside the add form the result shows as a short bubble in the corner.
  useEffect(() => { if (!feedback || tab === 'add') return; const t = setTimeout(() => setFeedback(''), 4500); return () => clearTimeout(t); }, [feedback, tab]);
  const [error, setError] = useState('');

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError('');
    try {
      await fn();
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function showFriendMenu(e: React.MouseEvent, f: any) {
    e.preventDefault();
    e.stopPropagation();
    if (!onOpenMenu) {
      onRemove(f);
      return;
    }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.type === 'contextmenu' ? e.clientX : rect.right - 220;
    const y = e.type === 'contextmenu' ? e.clientY : rect.bottom + 6;
    onOpenMenu(Math.max(12, x), y, [
      { label: tr('Message', 'Nachricht senden'), icon: <Mail size={16} />, run: () => onMessage(f.person) },
      { label: tr('Open Profile', 'Profil öffnen'), icon: <User size={16} />, run: () => onProfile(f.person) },
      ...(onRename?[{label:tr('Change personal name','Persönlichen Namen ändern'),icon:<Pencil size={16}/>,run:()=>onRename(f.person)}]:[]),
      {label:contactMuted(f.person.id)?tr('Unmute notifications','Benachrichtigungen aktivieren'):tr('Mute notifications','Benachrichtigungen stummschalten'),icon:<BellOff size={16}/>,run:()=>void run(async()=>{await request('/contacts/'+f.person.id+'/preferences','PATCH',{notificationsMuted:!contactMuted(f.person.id)})})},
      {
        label: tr('Remove Friend', 'Freund entfernen'),
        danger: true,
        divider: true,
        icon: <UserMinus size={16} />,
        run: () => onRemove(f)
      },
      {
        label: tr('Block User', 'Person blockieren'),
        danger: true,
        icon: <Ban size={16} />,
        run: () => {
          void run(async () => {
            await request('/blocks', 'POST', { userId: f.person.id });
          });
        }
      },
      {
        label: tr('Copy ID', 'ID kopieren'),
        divider: true,
        icon: <Copy size={16} />,
        run: () => {
          void copyToClipboard(f.person.id);
        }
      }
    ]);
  }

  const online = (p: any) => data.online.includes(p.id) && p.status !== 'invisible';
  const rows = (
    tab === 'blocked'
      ? data.blocks.map((b: any) => ({ id: b.targetId, person: b.person }))
      : data.friends.filter((f: any) =>
          tab === 'pending'
            ? f.state === 'pending'
            : f.state === 'accepted' && (tab !== 'online' || online(f.person))
        )
  ).filter((f: any) =>
    (shownName(f.person) + ' ' + f.person?.username).toLowerCase().includes(query.toLowerCase())
  );

  return (
    <section className="friends-screen" key={tab}>
      {tab === 'add' ? (
        <div className="friend-add-page">
          <h2>{tr('Add Friend', 'Freund hinzufügen')}</h2>
          <p>{tr('You can add friends with their MALVO username.', 'Du kannst Freunde über ihren MALVO-Nutzernamen hinzufügen.')}</p>
          <form
            className="inline-friend-form"
            onSubmit={e => {
              e.preventDefault();
              run(async () => {
                await request('/friends', 'POST', { username });
                setFeedback(tr('Friend request sent.', 'Freundschaftsanfrage gesendet.'));
                setUsername('');
              });
            }}
          >
            <input
              aria-label={tr('Username for friend request', 'Nutzername für Freundschaftsanfrage')}
              placeholder={tr('Enter a username...', 'Nutzernamen eingeben...')}
              value={username}
              onChange={e => {
                setUsername(e.target.value);
                setFeedback('');
              }}
              maxLength={25}
              required
            />
            <button className="primary" disabled={busy || !username.trim()}>
              <UserPlus size={16} /> {tr('Send Request', 'Anfrage senden')}
            </button>
          </form>
          {feedback && (
            <p className="inline-success" role="status">
              <Check size={16} /> {feedback}
            </p>
          )}
        </div>
      ) : (
        <>
          <label className="friends-wide-search">
            <Search size={18} />
            <input
              aria-label={tr('Search friends', 'Freunde suchen')}
              placeholder={
                tab === 'online'
                  ? tr('Search online...', 'Online durchsuchen...')
                  : tab === 'blocked'
                  ? tr('Search blocked...', 'Blockierte durchsuchen...')
                  : tr('Search friends...', 'Freunde durchsuchen...')
              }
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </label>
          <h2 className="friends-count">
            {tab === 'pending'
              ? tr('Pending Requests', 'Ausstehende Anfragen')
              : tab === 'blocked'
              ? tr('Blocked Users', 'Blockierte Nutzer')
              : tab === 'online'
              ? tr('Online Friends', 'Online-Freunde')
              : tr('All Friends', 'Alle Freunde')}{' '}
            ({rows.length})
          </h2>
          <div className="friend-list">
            {rows.map((f: any) => {
              const avatarSrc = defaultAvatarSrc(f.person);
              return (
                <div
                  className="friend-row"
                  key={f.id}
                  onContextMenu={e => showFriendMenu(e, f)}
                >
                  <button
                    className="friend-avatar"
                    style={{
                      background: f.person.color || '#2a2a36'
                    }}
                    onClick={() => onProfile(f.person)}
                  >
                    {avatarSrc ? (
                      <img src={avatarSrc} alt={shownName(f.person)} />
                    ) : (
                      shownName(f.person).slice(0, 2).toUpperCase()
                    )}
                    {online(f.person) && <i className={'status-dot status-' + (f.person.status || 'online')} />}
                  </button>
                  <div className="friend-name" onClick={() => onProfile(f.person)} style={{ cursor: 'pointer' }}>
                    <b>{shownName(f.person)}<StaffMark person={f.person} size={14}/> <IdentityBadges person={f.person}/></b>
                    <small>
                      {tab === 'pending'
                        ? f.recipientId === me.id
                          ? tr('Incoming Friend Request', 'Eingehende Anfrage')
                          : tr('Outgoing Friend Request', 'Ausgehende Anfrage')
                        : '@' + f.person.username}
                    </small>
                  </div>
                  {tab === 'blocked' ? (
                    <button
                      className="secondary"
                      disabled={busy}
                      onClick={() =>
                        run(async () => {
                          await request('/blocks/' + f.id, 'DELETE');
                        })
                      }
                    >
                      {tr('Unblock', 'Freigeben')}
                    </button>
                  ) : tab === 'pending' ? (
                    <>
                      {f.recipientId === me.id && (
                        <button
                          title={tr('Accept', 'Annehmen')}
                          disabled={busy}
                          onClick={() =>
                            run(async () => {
                              await request('/friends/' + f.id + '/accept', 'POST', {});
                              setFeedback(shownName(f.person)+tr(' is now your friend. You can chat now!',' ist jetzt mit dir befreundet. Ihr könnt jetzt chatten!'));
                            })
                          }
                        >
                          <Check size={19} />
                        </button>
                      )}
                      <button
                        title={f.recipientId === me.id ? tr('Decline', 'Ablehnen') : tr('Cancel', 'Abbrechen')}
                        disabled={busy}
                        onClick={() =>
                          run(async () => {
                            await request('/friends/' + f.id, 'DELETE');
                          })
                        }
                      >
                        <X size={19} />
                      </button>
                    </>
                  ) : (
                    <>
                      <button title={tr('Message', 'Nachricht')} onClick={() => onMessage(f.person)}>
                        <MessageCircle size={19} />
                      </button>
                      <button title={tr('More', 'Mehr')} onClick={e => showFriendMenu(e, f)}>
                        <MoreHorizontal size={19} />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          {!rows.length && (
            <div className="friends-quiet">
              <Users size={28} />
              <p>
                {query
                  ? tr('No matches found.', 'Keine Treffer gefunden.')
                  : tab === 'online'
                  ? tr('No friends are online right now.', 'Aktuell sind keine Freunde online.')
                  : tab === 'blocked'
                  ? tr('You have no blocked users.', 'Du hast keine blockierten Kontakte.')
                  : tab === 'pending'
                  ? tr('No pending friend requests.', 'Keine offenen Freundschaftsanfragen.')
                  : tr('No friends added yet.', 'Noch keine Freunde hinzugefügt.')}
              </p>
            </div>
          )}
        </>
      )}
      {tab !== 'add' && feedback && <div className="toast" role="status"><Check size={16}/>{feedback}</div>}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
